
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Receipt } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getPayments } from "@/services/edgeFunctionWrappers/paymentsManagementWrapper";
import { format } from "date-fns";
import { getUserGymId } from "@/utils/gymAuth";

interface ProfileInvoicesProps {
  userId?: string;
}

interface Invoice {
  id: string;
  amount: number;
  date: string;
  status: string;
  pdfUrl?: string;
}

export const ProfileInvoices: React.FC<ProfileInvoicesProps> = ({ userId }) => {
  const { t } = useTranslation(["profile", "common"]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const gymId = getUserGymId();
  

  const fetchInvoices = async () => {
    if (!userId) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await getPayments({
        gym_id: gymId,
        user_id: userId,
      });
        
      if (error) {
        throw error;
      }
      
      // Transform the data to match our Invoice interface
      const formattedInvoices = data ? data.map((item: any) => ({
        id: item.id,
        amount: item.amount,
        date: item.payment_date,
        status: item.status,
        pdfUrl: item.invoice_url || undefined
      })) : [];
      
      setInvoices(formattedInvoices);
      
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error(t("errors.loadingInvoices", { ns: "profile" }));
      
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, [userId]);

  const handleDownloadInvoice = (invoice: Invoice) => {
    if (invoice.pdfUrl) {
      // In a real implementation, this would download the actual PDF
      toast.info(t("info.downloadingInvoice", { ns: "profile" }));
    } else {
      toast.error(t("errors.invoiceNotAvailable", { ns: "profile" }));
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  return (
    <Card className="border shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>{t("invoices.title", { ns: "profile" })}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {isLoading ? (
            <p>{t("loading", { ns: "common" })}</p>
          ) : invoices.length === 0 ? (
            <div className="text-center p-6">
              <Receipt className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-1">{t("invoices.noInvoices", { ns: "profile" })}</h3>
              <p className="text-sm text-muted-foreground">
                {t("invoices.noInvoicesDesc", { ns: "profile" })}
              </p>
            </div>
          ) : (
            <div className="relative overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase bg-secondary/50">
                  <tr>
                    <th scope="col" className="px-4 py-3 rounded-l-lg">{t("invoices.invoice", { ns: "profile" })}</th>
                    <th scope="col" className="px-4 py-3">{t("invoices.date", { ns: "profile" })}</th>
                    <th scope="col" className="px-4 py-3">{t("invoices.amount", { ns: "profile" })}</th>
                    <th scope="col" className="px-4 py-3">{t("invoices.status", { ns: "profile" })}</th>
                    <th scope="col" className="px-4 py-3 rounded-r-lg"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(invoice => (
                    <tr key={invoice.id} className="bg-transparent border-b">
                      <td className="px-4 py-3">{invoice.id}</td>
                      <td className="px-4 py-3">{format(new Date(invoice.date), 'dd/MM/yyyy')}</td>
                      <td className="px-4 py-3">{formatCurrency(invoice.amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          invoice.status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' : 
                          invoice.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' : 
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDownloadInvoice(invoice)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
